// src/components/SubPayTermFields.js
// ---------------------------------------------------------------------------
// The Daily / Project inputs for ONE subcontractor on ONE job. Used by the
// Tech Assign picker (SubPayTermsInline) and the job's Sub Pay panel.
// Purely controlled: hands back the edited term, never writes Firestore.
// ---------------------------------------------------------------------------

import React from "react";
import { computeTermOwed, DEFAULT_DAILY_RATE, isContinuationJob } from "../utils/subPayTerms";
import "./SubPayPanel.css";

export const fmtMoney = (v) =>
  (Number(v) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const SubPayTermFields = ({ job, term, onChange, disabled = false, projectHeldOn = "" }) => {
  const calc = computeTermOwed(job, term);
  const set = (patch) => onChange({ ...term, ...patch });

  if (calc.skipped === "recurring") {
    return (
      <div className="spp-note">
        This is a recurring job. Daily / project pay works on one-time jobs. For recurring visits use
        Sub Pay on the Job Estimate Spreadsheet.
      </div>
    );
  }

  return (
    <div className="spp-terms">
      <div className="spp-mode" role="radiogroup" aria-label="Pay type">
        {[
          ["daily", "Daily"],
          ["project", "Project"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={calc.mode === value}
            className={`spp-mode__btn${calc.mode === value ? " spp-mode__btn--on" : ""}`}
            disabled={disabled}
            onClick={() => set({ mode: value })}
          >
            {label}
          </button>
        ))}
      </div>

      {calc.mode === "daily" ? (
        <div className="spp-grid">
          <label>
            <span>Workers</span>
            <input
              type="number"
              min="0"
              step="1"
              value={term.workers ?? 1}
              disabled={disabled}
              onChange={(e) => set({ workers: e.target.value })}
            />
          </label>
          <label>
            <span>$ per day / worker</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={term.ratePerWorker ?? DEFAULT_DAILY_RATE}
              disabled={disabled}
              onChange={(e) => set({ ratePerWorker: e.target.value })}
            />
          </label>
          <label>
            <span>Days</span>
            <input
              type="number"
              min="0"
              step="0.5"
              value={calc.own ? term.days ?? "" : ""}
              placeholder={`${calc.autoDays} (job dates)`}
              disabled={disabled}
              onChange={(e) => set({ days: e.target.value })}
            />
          </label>
          <div className="spp-calc">
            {calc.workers} × {fmtMoney(calc.rate)} × {calc.days} day{calc.days === 1 ? "" : "s"} ={" "}
            <b>{fmtMoney(calc.amount)}</b>
            {!calc.autoDays && <div className="spp-warn">This job has no date yet, so it counts 0 days.</div>}
          </div>
        </div>
      ) : calc.own ? (
        <div className="spp-grid">
          <label>
            <span>Project price ($)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={term.projectAmount ?? ""}
              disabled={disabled}
              onChange={(e) => set({ projectAmount: e.target.value })}
            />
          </label>
          <div className="spp-calc">
            Owed once for the whole job, including every Continue: <b>{fmtMoney(calc.amount)}</b>
          </div>
          {isContinuationJob(job) && (
            <div className="spp-warn" style={{ gridColumn: "1 / -1" }}>
              This job was continued from{" "}
              <b>WO {job.continuedFromWO || job.projectContinuedFromWO || "an earlier job"}</b>. If this price is for
              the WHOLE job, enter it on the first job instead and use <b>Copy to continued jobs</b> there, so it is
              only counted once. Enter it here only if this work order has its own separate price.
            </div>
          )}
        </div>
      ) : (
        <div className="spp-note">
          Project price <b>{fmtMoney(calc.projectAmount)}</b> rolled over from{" "}
          {projectHeldOn ? <b>WO {projectHeldOn}</b> : "the first job"}. It is owed once there, so this job adds $0.
          Change the price on that job, or switch to Daily to pay this day separately.
        </div>
      )}
    </div>
  );
};

export default SubPayTermFields;
